import type { FC } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { PixelPlaceholderLoader, ChangeValue } from "@similarweb/ui-components";

import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { NO_DATA_SIGN } from "app/filters/const";
import { percentageSignFilter } from "app/filters/ngFilters";
import { normalizeForMinValue } from "app/pages/website-analysis/traffic-sources/search/common/utils";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import type { IDataRow } from "app/common/keyword-research/keywords-overview/dataRowTypes";

import {
    DataDescription,
    DataDescriptionLoader,
    DataGrowthBasis,
    DataRowContainer,
    DataRowContent,
    DataRowTitle,
    DataRowValue,
    DataRowWithAddToDashboarTitle,
    DataTitle,
    DataValue,
} from "./DataRowStyledComponents";

const MODEL_TYPE = "fromKeyword";

export const DataRow: FC<IDataRow> = (props) => {
    const {
        isLoading,
        title,
        tooltip,
        value,
        change,
        description,
        filter,
        displayNoData,
        invertChangeColor = false,
        addToDashboardConfig,
    } = props;
    const { translate } = useTranslate();
    const isPositive = change > 0;
    let valueToDisplay: string | number;
    if (!value && displayNoData) {
        valueToDisplay = NO_DATA_SIGN;
    } else {
        valueToDisplay = typeof value === "number" ? filter(normalizeForMinValue(value)) : value;
    }

    const DataRowTitleComponent = addToDashboardConfig ? DataRowWithAddToDashboarTitle : DataRowTitle;

    return (
        <DataRowContainer>
            <DataRowContent>
                <FlexRow
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <BoxTitle
                        Container={DataRowTitleComponent}
                        tooltip={translate(tooltip)}
                    >
                        <DataTitle>{translate(title)}</DataTitle>
                    </BoxTitle>
                    {addToDashboardConfig ? (
                        <AddToDashboard
                            modelType={MODEL_TYPE}
                            {...addToDashboardConfig}
                        />
                    ) : null}
                </FlexRow>
                {isLoading ? (
                    <>
                        <PixelPlaceholderLoader
                            width={130}
                            height={24}
                        />
                        {description ? (
                            <DataDescriptionLoader
                                width={55}
                                height={16}
                            />
                        ) : null}
                    </>
                ) : (
                    <>
                        <DataRowValue>
                            <DataValue>{valueToDisplay}</DataValue>
                            {change ? (
                                <FlexRow alignItems="center">
                                    <ChangeValue
                                        value={percentageSignFilter()(Math.abs(change), 2)}
                                        isDecrease={!isPositive}
                                        className="data-row-change-value"
                                        invertColors={invertChangeColor}
                                    />
                                    <DataGrowthBasis>
                                        ({translate("keyword.analysis.overview.page.v2.data_row.growth_basis")})
                                    </DataGrowthBasis>
                                </FlexRow>
                            ) : null}
                        </DataRowValue>
                        {description ? <DataDescription>{description}</DataDescription> : null}
                    </>
                )}
            </DataRowContent>
        </DataRowContainer>
    );
};
